import { toJS } from 'mobx';
import { types as t } from 'mst-effect';
import { withRunInAction } from 'utils/withRunInAction';
import { Input, Data, Struct } from 'api/grpc/resources';
import { PER_PAGE } from 'modules/InputViewer/utils/constants';
import { MultiSearchResponse } from 'api/grpc/service';
import { either } from 'fp-ts';
import { mapOr, pipe } from 'utils/fp';
import isEqual from 'lodash/isEqual';
import toastMessage from 'components/ToastMessage/ToastMessage';
import { grpcClient } from 'api/grpc/client';
import { getInputPagedIndex, getPageNumberByIndex } from 'modules/InputViewer/utils/helpers';
import { getRespPage } from 'modules/InputsManager/helpers/inputListStoreHelpers';
import { grpcToRestInput } from 'utils/grpc/grpcToRest';
import { convertJsInputToGrpcInput } from 'utils/grpc/jsToGrpc';

type InputMapData = {
  pageNumber: number;
  index: number;
  input: CF.API.Inputs.Input;
};

enum Mode {
  Create,
  Edit,
}

export const InputsListStore = t
  .compose(
    withRunInAction(),
    t.model('InputsListStore', {
      inputsListRecords: t.map(t.frozen()),
      scoresInputsListRecord: t.map(t.frozen()),
      hasReachedEnd: t.maybeNull(t.boolean),
      lastPage: t.maybeNull(t.number),
      currentPage: t.optional(t.number, 1),
      pagesStartedLoad: t.optional(t.array(t.number), []),
      currentInput: t.maybeNull(t.frozen<Input>()),
      currentIndex: t.optional(t.number, -1),
      isInputDeletionInProgress: t.optional(t.boolean, false),
    }),
  )
  .views((self) => ({
    get lastPageNumber() {
      const inputList = toJS(self.inputsListRecords);
      const inputListKeys = Array.from(inputList.keys());
      const sortedInputListKeys = inputListKeys.sort((a, b) => Number(a) - Number(b));
      return Number(sortedInputListKeys[sortedInputListKeys.length - 1]);
    },
  }))
  .views((self) => ({
    get inputsLength() {
      if (!self.lastPageNumber) return 0;

      if (self.hasReachedEnd) {
        const lastPage = self.inputsListRecords.get(String(self.lastPageNumber));
        return (self.lastPageNumber - 1) * PER_PAGE + (lastPage?.length || 0);
      }

      return self.lastPageNumber * PER_PAGE;
    },
    get listItems() {
      let page = 1;
      const list: Input[] = [];
      while (self.inputsListRecords.get(String(page))) {
        list.push(...self.inputsListRecords.get(String(page)));
        page += 1;
      }
      return list;
    },
    get isAllowedToDraw() {
      const inputData = self.currentInput?.getData();

      return inputData && (inputData.hasImage() || inputData.hasVideo());
    },
  }))
  .actions((self) => ({
    resetInputsListStore() {
      self.inputsListRecords.clear();
      self.scoresInputsListRecord.clear();
      self.hasReachedEnd = null;
      self.lastPage = null;
      self.currentPage = 1;
      self.pagesStartedLoad.replace([]);
      self.currentInput = null;
      self.currentIndex = -1;
    },
    getInputScore(inputId: string) {
      return self.scoresInputsListRecord.get(inputId);
    },
    removePageFromLoaded(page: number) {
      self.pagesStartedLoad.replace(self.pagesStartedLoad.filter((p) => p !== page));
      self.hasReachedEnd = null;
    },
    arePagesEqual(pageNumber: number, listE: either.Either<Error, MultiSearchResponse>): boolean {
      const respPage = getRespPage(listE);
      const currentPage = self.inputsListRecords.get(String(pageNumber));
      return isEqual(respPage, currentPage);
    },
    areScoresEqual(listE: either.Either<Error, MultiSearchResponse>): boolean {
      const newScores = mapOr(listE, (r) => r.getHitsList().map((hit) => ({ id: hit.getInput()?.getId() || '', score: hit.getScore() ?? 1 })), null);
      if (!newScores) return false;

      const currentScores = self.scoresInputsListRecord.toJSON();

      for (const { id, score } of newScores) {
        if (score !== currentScores[id]) {
          return false;
        }
      }
      return true;
    },
    checkIfReachEnd(listE: either.Either<Error, MultiSearchResponse>) {
      const respPage = getRespPage(listE);
      if (respPage && respPage?.length && respPage.length < PER_PAGE) {
        self.hasReachedEnd = true;
      }
    },
    clearAllInputsRecords() {
      self.inputsListRecords.clear();
      self.scoresInputsListRecord.clear();
    },
    updateInputListRecordItem(pageNumber?: number, inputList?: Input[]) {
      if (pageNumber && inputList) {
        self.inputsListRecords.set(String(pageNumber), inputList);
      }
    },
    getInputMapDataByInputId(inputId: string) {
      let inputMapData: InputMapData | undefined;

      self.inputsListRecords.forEach((inputList: Input[], pageNumber) => {
        inputList.forEach((input, index) => {
          if (input.getId() === inputId) {
            inputMapData = {
              pageNumber: +pageNumber,
              index,
              input: grpcToRestInput(input),
            };
          }
        });
      });

      return inputMapData;
    },
    getInputObjectAndIndexById(inputId: string): { input: Input; index: number } | null {
      let foundItem = null;

      for (const [pageNumber, inputList] of Array.from(self.inputsListRecords.entries())) {
        for (const [index, input] of inputList.entries()) {
          if (input.getId() === inputId) {
            foundItem = { input, index: (Number(pageNumber) - 1) * PER_PAGE + index };
            break;
          }
        }
        if (foundItem) {
          break;
        }
      }
      return foundItem;
    },
    getInputObjectByIndex(index: number): Input | undefined {
      const pageNumber = getPageNumberByIndex(index);
      const inputPagedIndex = getInputPagedIndex(index);
      if (pageNumber !== undefined && inputPagedIndex !== undefined) {
        const page = self.inputsListRecords.get(String(pageNumber));
        if (page) {
          return page[inputPagedIndex];
        }
      }

      return undefined;
    },
  }))
  .actions((self) => ({
    setInputsListPage(page: number, listE: either.Either<Error, MultiSearchResponse>): void {
      if (page === 1) self.scoresInputsListRecord.clear();
      return pipe(
        listE,
        either.match(
          // onLeft
          () => {
            console.warn('setInputsListPage: something went wrong');
          },
          // onRight
          (resp) => {
            self.runInAction(() => {
              const inputs = resp.getHitsList().map((hit) => {
                // non-null assertion is fine because the API always returns an input for each hit
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const score = hit.getScore();
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const input = hit.getInput()!;
                const inputId = input?.getId();

                if (inputId) self.scoresInputsListRecord.set(String(inputId), score);

                return input;
              });
              if (!inputs?.length) {
                // list is empty then reached end
                self.hasReachedEnd = true;
                const newLastPage = page - 1;
                if (!self.lastPage || self.lastPage > newLastPage) {
                  self.lastPage = newLastPage;
                }
              } else {
                self.updateInputListRecordItem(Number(page), inputs);
                if (inputs?.length < PER_PAGE) {
                  // has reached end if less than PER_PAGE length
                  self.hasReachedEnd = true;
                  self.lastPage = page;
                }
                if (!self.currentPage || self.currentPage < page) {
                  self.currentPage = page;
                }
              }
            });
          },
        ),
      );
    },
  }))
  .actions((self) => ({
    updateCurrentInput(index: number) {
      const currentInput = self.getInputObjectByIndex(index);
      if (currentInput) {
        self.currentInput = currentInput;
        self.currentIndex = index;
      }
    },
    removeCurrentInput() {
      self.currentInput = null;
    },
  }))
  .actions((self) => ({
    async updateInputMetadata(
      inputId: string,
      metadata: CF.API.Metadata | undefined,
      appId: string,
      userId: string,
      mode: Mode,
      action: CF.API.Action = 'overwrite',
    ) {
      const isCreating = mode === Mode.Create;
      const grpcInput = new Input();

      grpcInput.setId(inputId);
      if (metadata) {
        grpcInput.setData(new Data().setMetadata(Struct.fromJavaScript(metadata)));
      } else {
        grpcInput.setData(new Data());
      }

      const response = await grpcClient.patchInputs({
        appId,
        userOrOrgId: userId,
        inputList: [grpcInput],
        action,
      });

      pipe(
        response,
        either.fold(
          () => {
            const verb = isCreating ? 'adding' : 'editing';

            toastMessage.error(`There was an error while ${verb} the metadata.`);
          },
          () => {
            const message = isCreating ? 'Metadata created!' : 'Metadata updated!';
            const inputMapData = self.getInputMapDataByInputId(inputId);

            toastMessage.success(message);

            if (inputMapData) {
              const { pageNumber, index, input } = inputMapData;
              const currentInputList = self.inputsListRecords.get(String(pageNumber));
              const updatedInput = {
                ...input,
                data: {
                  ...input.data,
                  metadata,
                },
              };
              const newInput = convertJsInputToGrpcInput(updatedInput);
              const updatedInputList = [...currentInputList.slice(0, index), newInput, ...currentInputList.slice(index + 1)];

              // TODO
              self.updateInputListRecordItem(pageNumber, updatedInputList);
              self.updateCurrentInput(index);
            }
          },
        ),
      );
    },
  }));
